// ══════════════════════════════════════════════════════════════════════════════
// SELDOR — Login Page
// ══════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { Eye, Globe, AlertCircle } from 'lucide-react';
import { signInWithPassword } from '@lib/supabase';
import { useTheme } from '@contexts/ThemeContext';
import { useLang } from '@contexts/LangContext';
import { Card, Button, Input } from '@components/ui/Primitives';

export function LoginPage() {
  const { theme, themeName } = useTheme();
  const { t, lang, toggleLang, dir } = useLang();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setError('');
    setLoading(true);
    const { error: err } = await signInWithPassword(email, password);
    if (err) {
      setError(t.invalidCredentials);
      setLoading(false);
      return;
    }
    // Auth context will handle the rest via onAuthChange
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && email && password && !loading) handleSubmit();
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: themeName === 'dark'
        ? 'linear-gradient(145deg, #040614, #0A0E3A 50%, #060918)'
        : 'linear-gradient(145deg, #E8ECF4, #F4F5F9 50%, #FAFBFE)',
      padding: 20,
      direction: dir
    }}>
      <div style={{ width: '100%', maxWidth: 420, position: 'relative' }}>
        {/* Decorative glow */}
        <div style={{
          position: 'absolute',
          width: 300,
          height: 300,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${theme.primaryDim}, transparent 70%)`,
          top: -100,
          [dir === 'rtl' ? 'left' : 'right']: -80,
          pointerEvents: 'none',
          filter: 'blur(40px)'
        }} />

        <Card style={{ padding: '44px 36px', position: 'relative', zIndex: 1 }}>
          {/* Language toggle */}
          <div style={{
            position: 'absolute',
            top: 16,
            [dir === 'rtl' ? 'left' : 'right']: 16
          }}>
            <div
              onClick={toggleLang}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                cursor: 'pointer',
                background: theme.bgInput,
                borderRadius: 6,
                padding: '5px 10px',
                border: `1px solid ${theme.border}`
              }}
            >
              <Globe size={13} color={theme.textSecondary} />
              <span style={{ fontSize: 11, fontWeight: 600, color: theme.textSecondary }}>
                {lang === 'ar' ? 'EN' : 'AR'}
              </span>
            </div>
          </div>

          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
            <div style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: `linear-gradient(135deg, ${theme.primary}, #6C8EFF)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <Eye size={22} color="#fff" />
            </div>
            <div>
              <div style={{ fontSize: 22, fontWeight: 800, color: theme.text, letterSpacing: -0.3 }}>
                {t.brandName}
              </div>
            </div>
          </div>

          {/* Tagline */}
          <div style={{
            fontSize: 12,
            color: theme.textTertiary,
            marginBottom: 28,
            fontStyle: 'italic',
            lineHeight: 1.6
          }}>
            "{t.tagline}"
          </div>

          {/* Welcome */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: theme.text, marginBottom: 4 }}>
              {t.welcome}
            </div>
            <div style={{ fontSize: 13, color: theme.textSecondary }}>
              {t.signInPrompt}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div style={{
              background: theme.dangerDim,
              border: `1px solid ${theme.danger}30`,
              borderRadius: 8,
              padding: '10px 14px',
              marginBottom: 16,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}>
              <AlertCircle size={14} color={theme.danger} />
              <span style={{ color: theme.danger, fontSize: 13 }}>{error}</span>
            </div>
          )}

          {/* Form fields */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 24 }}>
            <div>
              <label style={{
                fontSize: 12,
                fontWeight: 600,
                color: theme.textSecondary,
                marginBottom: 6,
                display: 'block'
              }}>
                {t.email}
              </label>
              <Input
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@company.com"
                type="email"
                onKeyDown={handleKeyDown}
              />
            </div>
            <div>
              <label style={{
                fontSize: 12,
                fontWeight: 600,
                color: theme.textSecondary,
                marginBottom: 6,
                display: 'block'
              }}>
                {t.password}
              </label>
              <Input
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                type="password"
                onKeyDown={handleKeyDown}
              />
            </div>
          </div>

          {/* Submit */}
          <Button
            onClick={handleSubmit}
            disabled={loading || !email || !password}
            fullWidth
            size="lg"
          >
            {loading ? t.signingIn : t.signIn}
          </Button>

          {/* Footer note */}
          <div style={{
            marginTop: 20,
            textAlign: 'center',
            fontSize: 11,
            color: theme.textTertiary
          }}>
            {t.accessRestricted}
          </div>
        </Card>
      </div>
    </div>
  );
}
